import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Image as ImageIcon, Trees, Wand2, Settings, BarChart, Layout, Search, Database, Video, LayoutList, BookOpen, Tv, Film, Music, LogOut } from 'lucide-react';
import { logoutUser } from '../../services/auth';

interface NavItemProps {
  to: string;
  label: string;
  icon: React.ReactNode;
}

function NavItem({ to, label, icon }: NavItemProps) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
          isActive
            ? 'bg-slate-100 text-slate-900 font-medium'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
        }`
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}

export default function DashboardLayout() {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.trim()) {
        navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="w-64 border-r border-slate-200 bg-white flex flex-col">
        <div className="p-4 border-b border-slate-200 flex flex-col gap-4">
          <div className="flex items-center justify-between px-2">
            <span className="text-lg font-semibold tracking-tight">HeritageOS</span>
            <button
              onClick={logoutUser}
              className="text-xs text-red-600 hover:text-red-800 hover:bg-red-50 p-1.5 rounded-md flex items-center gap-1 transition-colors"
              title="تسجيل الخروج"
            >
              <LogOut size={16} />
              <span>خروج</span>
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-300"
            />
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          <NavItem to="/" label="Dashboard" icon={<LayoutDashboard size={20} />} />
          <NavItem to="/homepage-builder" label="Homepage Builder" icon={<Layout size={20} />} />
          <NavItem to="/media" label="Media Library (DAM)" icon={<ImageIcon size={20} />} />
          <NavItem to="/heritage" label="Heritage Tree" icon={<Trees size={20} />} />
          <NavItem to="/content" label="Content Wizard" icon={<Wand2 size={20} />} />
          <div className="px-3 pt-4 pb-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">Stories App</div>
          <NavItem to="/vod-stories" label="Stories List" icon={<LayoutList size={20} />} />
          <NavItem to="/vod/new" label="Add New Story" icon={<BookOpen size={20} />} />
          <NavItem to="/vod/channels" label="Channels & Narrators" icon={<Tv size={20} />} />
          <NavItem to="/music" label="Background Music" icon={<Music size={20} />} />
          
          <div className="px-3 pt-4 pb-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">Android TV App</div>
          <NavItem to="/movies-series" label="Movies & Series" icon={<Film size={20} />} />
          <NavItem to="/movies/new" label="Add Movie / Series" icon={<Video size={20} />} />
          <NavItem to="/movies/bulk-import" label="Bulk Import VOD" icon={<Database size={20} />} />
          <NavItem to="/iptv" label="Live TV & Radio" icon={<Tv size={20} />} />
          <NavItem to="/ai-jobs" label="AI Jobs" icon={<Wand2 size={20} />} />
          <NavItem to="/analytics" label="Analytics" icon={<BarChart size={20} />} />
        </nav>
        <div className="p-4 border-t border-slate-200 flex flex-col gap-1">
          <NavItem to="/settings" label="Settings" icon={<Settings size={20} />} />
          <NavItem to="/migration" label="Data Migration" icon={<Database size={20} />} />
          <NavItem to="/api-docs" label="API Documentation" icon={<BookOpen size={20} />} />
          <button
            onClick={logoutUser}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 mt-2 rounded-md bg-red-50 text-red-700 hover:bg-red-100 transition-colors font-medium text-sm border border-red-200"
          >
            <LogOut size={18} />
            <span>تسجيل الخروج (Logout)</span>
          </button>
        </div>
      </aside>
      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}
